/**
 * GitHub repo setup for PR scanning.
 *
 * Uses `gh secret set` to push CLAUDE_CODE_OAUTH_TOKEN + SYNKRO_API_KEY
 * as repo secrets, then writes .github/workflows/synkro.yml to the local
 * clone if present.
 */
import { existsSync, mkdirSync, writeFileSync } from 'node:fs';
import { execSync } from 'node:child_process';
import { join } from 'node:path';
import { SYNKRO_WORKFLOW_YAML, WORKFLOW_PATH } from './workflowTemplate.js';

export interface GitHubAuthOptions {
  token: string;
}

function ghSecretSet(token: string, owner: string, repo: string, name: string, value: string): void {
  execSync(`gh secret set ${name} --repo ${owner}/${repo} --body -`, {
    input: value,
    env: { ...process.env, GH_TOKEN: token },
    stdio: ['pipe', 'ignore', 'pipe'],
    timeout: 30_000,
  });
}

/**
 * List repos the token has access to.
 */
export async function listAccessibleRepos(opts: GitHubAuthOptions): Promise<Array<{ owner: string; repo: string; full_name: string }>> {
  const repos: Array<{ owner: string; repo: string; full_name: string }> = [];
  let page = 1;
  while (page <= 5) {  // cap pagination
    const url = `https://api.github.com/user/repos?per_page=100&page=${page}&affiliation=owner,collaborator`;
    const resp = await fetch(url, {
      headers: {
        Authorization: `Bearer ${opts.token}`,
        Accept: 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
      },
    });
    if (!resp.ok) {
      throw new Error(`GitHub API ${resp.status} listing repos`);
    }
    const data = await resp.json() as Array<{ full_name: string; owner: { login: string }; name: string }>;
    if (data.length === 0) break;
    for (const r of data) {
      repos.push({ owner: r.owner.login, repo: r.name, full_name: r.full_name });
    }
    if (data.length < 100) break;
    page++;
  }
  return repos;
}

export async function pushSecretsToRepo(
  opts: GitHubAuthOptions,
  owner: string,
  repo: string,
  secrets: { claudeCodeOauthToken?: string; synkroApiKey: string },
): Promise<void> {
  try { execSync('gh --version', { stdio: 'ignore', timeout: 5000 }); } catch {
    throw new Error('GitHub CLI (gh) not found. Install it: https://cli.github.com');
  }
  if (secrets.claudeCodeOauthToken) {
    ghSecretSet(opts.token, owner, repo, 'CLAUDE_CODE_OAUTH_TOKEN', secrets.claudeCodeOauthToken);
  }
  ghSecretSet(opts.token, owner, repo, 'SYNKRO_API_KEY', secrets.synkroApiKey);
}

/**
 * Write the workflow YAML to a local repo clone (if the user is in one).
 * Returns the absolute path written, or null if cwd isn't a git repo.
 */
export function writeWorkflowFile(repoRootPath: string): string | null {
  const workflowDir = join(repoRootPath, '.github', 'workflows');
  mkdirSync(workflowDir, { recursive: true });
  const workflowFile = join(workflowDir, 'synkro.yml');
  writeFileSync(workflowFile, SYNKRO_WORKFLOW_YAML, 'utf-8');
  return workflowFile;
}

/**
 * Find the git repo root for a given cwd. Returns null if not in a git repo.
 */
export function findGitRoot(startCwd: string): string | null {
  let cur = startCwd;
  while (cur && cur !== '/') {
    if (existsSync(join(cur, '.git'))) return cur;
    const parent = join(cur, '..');
    if (parent === cur) break;
    cur = parent;
  }
  return null;
}

export const SECRET_NAMES = {
  CLAUDE_OAUTH: 'CLAUDE_CODE_OAUTH_TOKEN',
  SYNKRO_API_KEY: 'SYNKRO_API_KEY',
} as const;

export const WORKFLOW_RELATIVE_PATH = WORKFLOW_PATH;
